import { BreadcrumbsData, NeutralButton, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { PayloadTooLarge } from '@systemeio/util'
import { SendTestEmailModal } from 'modules/mailing/components/send-test-email-modal'
import { getEmailStatisticsUrl } from 'modules/mailing/email-statistics/utils/get-email-statistics-url'
import { DuplicateNewsletterModal } from 'modules/newsletters/single/components/duplicate-newsletter-modal'
import { SaveAndSendNewsletterModal } from 'modules/newsletters/single/components/save-and-send-newsletter-modal'
import { ScheduleNewsletterModal } from 'modules/newsletters/single/components/schedule-newsletter-modal'
import { newsletterDefaultErrors } from 'modules/newsletters/single/constants/newsletter-default-errors'
import { useGetNewsletterView } from 'modules/newsletters/single/hooks/use-get-newsletter-view'
import { useNewsletterContactsSettings } from 'modules/newsletters/single/hooks/use-newsletter-contacts-settings'
import { useUpdateNewsletter } from 'modules/newsletters/single/hooks/use-update-newsletter'
import { NewsletterErrorResponseContentInterface } from 'modules/newsletters/single/types/error-response-content-interface'
import { NewsletterId } from 'modules/newsletters/single/types/newsletter-interface'
import { NewsletterViewApiInterface } from 'modules/newsletters/single/types/newsletter-view-interface'
import { getNewslettersListNextjsUrl } from 'modules/newsletters/single/utils/get-newsletters-list-nextjs-url'
import { setNewsletterEmailErrors } from 'modules/newsletters/single/utils/set-newsletter-email-errors'
import { transformToNewsletterData } from 'modules/newsletters/single/utils/transform-to-newsletter-data'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import { useEffect, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import { ButtonWithDropdown } from 'shared/components/buttin-with-dropdown/button-with-dropdown'
import { ButtonWithDropdownTypeEnum } from 'shared/components/buttin-with-dropdown/enums/button-with-dropdown-type-enum'
import DashboardMailEditor from 'shared/components/dashboard-mail-editor'
import { useMailingState } from 'shared/components/dashboard-mail-editor/hooks/use-mailing-state'
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email'
import { TempStateType } from 'shared/components/dashboard-mail-editor/types/temp-state-type'
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data'
import { validateEmailBeforeSave } from 'shared/components/dashboard-mail-editor/utils/validate-email-before-save'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { deepCompare } from 'shared/utils/deepCompare'
import { CreateSplitTestModal } from '../split-test/components/create-split-test-modal'
import { CloseIcon, EventIcon, SendEmailIcon, StatisticsIcon, TestTubeIcon } from '@systemeio/ui/icons'

interface NewsletterSingleProps {
  newsletterId: NewsletterId
  newsletterData: NewsletterViewApiInterface
}

export const NewsletterSingle = ({ newsletterId, newsletterData }: NewsletterSingleProps) => {
  const { t } = useLocoTranslation()
  const { isRestrictedUser } = useUser()

  const [isFetching, setIsFetching] = useState<boolean>(false)

  const {
    isFetchingChooseEmailTemplate,
    onChangeEmail,
    tempState,
    setAttachmentsTempState,
    attachmentsTempState,
    errors,
    setErrors,
    setDefaultErrors,
  } = useMailingState({
    mailing: newsletterData.mailing,
    onSave: data => onSave(data, { throwError: true }),
    defaultErrors: newsletterDefaultErrors,
  })

  const { contactsSettings, setContactsSettings } = useNewsletterContactsSettings(newsletterData)

  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)

  const { fetcherUpdateNewsletter } = useUpdateNewsletter<NewsletterViewApiInterface>(newsletterId)

  const { mutateNewslettersData } = useGetNewsletterView({
    newsletterId,
  })
  const { tags: listOfTags } = useTagsAll()

  const isLoading = isFetching || isFetchingChooseEmailTemplate

  const isTagsSelected = !!contactsSettings.tags.included.length

  const breadcrumbsData: BreadcrumbsData[] = [
    {
      caption: t('dashboard.newsletter.newsletters'),
      href: getNewslettersListNextjsUrl(),
      isStatic: true,
    },
    {
      caption: newsletterData.mailing.currentEmail.subject,
    },
  ]

  const { UnsavedEmailConfirmationModal, removeDbRecord } = useUnsavedEmail({
    mailingId: newsletterData.mailing.id,
    currentBody: tempState?.body,
    initialBody: newsletterData.mailing.currentEmail.body,
    showImmediately: true,
    onPaste: data => onChangeEmail({ ...tempState, body: data }),
  })

  const onSave = async (newData: TempStateType, options?: { throwError?: boolean }) => {
    try {
      setDefaultErrors()
      validateEmailBeforeSave(newData.fromEmail, setErrors, t)
      setIsFetching(true)

      const responseData = await fetcherUpdateNewsletter({
        tags: contactsSettings.tags.included,
        excludedTags: contactsSettings.tags.excluded,
        daysExists: contactsSettings.daysExists,
        mailing: {
          currentEmail: getMailingUpdateData(newData, attachmentsTempState),
        },
      })

      await mutateNewslettersData(
        transformToNewsletterData({
          listOfTags: listOfTags,
          attachmentsTempState: responseData.mailing.currentEmail.attachments,
          tempState: newData,
          contactsSettings,
          newsletterData,
        }),
        false,
      )

      toast.success(t('global.changes_saved'))
      setIsFetching(false)
      await removeDbRecord()
    } catch (e) {
      setIsFetching(false)
      toast.error(t('global.error'))
      if (e instanceof BadRequest) {
        const errors = e.errors as NewsletterErrorResponseContentInterface
        const currentMailing = !Array.isArray(errors.fields) ? errors.fields.mailing : undefined
        if (currentMailing) {
          setNewsletterEmailErrors(setErrors, currentMailing.currentEmail)
        }
        if (errors.common) {
          const commonErrors = errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else if (e instanceof PayloadTooLarge) {
        toast.error(t('core.error.payload_to_large_error_message'))
      }
      if (options?.throwError) {
        throw e
      }
    }
  }

  useEffect(() => {
    setIsDataSaved(
      deepCompare(
        transformToNewsletterData({
          listOfTags: listOfTags,
          tempState,
          attachmentsTempState,
          contactsSettings,
          newsletterData,
        }),
        newsletterData,
      ),
    )
  }, [attachmentsTempState, contactsSettings, listOfTags, newsletterData, tempState])

  return (
    <>
      <div className={`flex flex-col gap-5`}>
        <div className="flex flex-col items-center justify-between gap-5 sm:flex-row">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!newsletterData} />
          <div className={'flex gap-3'}>
            {newsletterData.sent ? (
              <>
                <PrimaryButton
                  as={LinkWithoutPrefetch}
                  aria-disabled={isLoading}
                  href={getEmailStatisticsUrl(newsletterData.mailing.id)}
                >
                  <StatisticsIcon className={'h-[20px] w-[20px]'} />
                  <span className="hidden lg:flex">
                    {t('dashboard.newsletter_editor.statistics')}
                  </span>
                </PrimaryButton>
                <DuplicateNewsletterModal newsletterId={newsletterId} isDisabled={isLoading} />
              </>
            ) : (
              <>
                {!newsletterData.scheduledAt && (
                  <CreateSplitTestModal newsletterId={newsletterId} isDisabled={isLoading} />
                )}
                <ButtonWithDropdown
                  options={[
                    {
                      name: 'SendTestEmailModal',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.save_and_test'),
                        icon: <TestTubeIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <SendTestEmailModal
                            mailingId={newsletterData.mailing.id}
                            onSave={() => onSave(tempState, { throwError: true })}
                            opened={opened}
                            onClose={onClose}
                            key={key}
                          />
                        )
                      },
                    },
                    {
                      name: 'SaveAndSendNewsletterModal',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.save_and_send'),
                        icon: <SendEmailIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <SaveAndSendNewsletterModal
                            opened={opened}
                            onClose={onClose}
                            key={key}
                            newsletterId={newsletterId}
                            onSave={() => onSave(tempState, { throwError: true })}
                            confirmationText={t('dashboard.newsletter_editor.send_confirm')}
                            isTagsSelected={isTagsSelected}
                          />
                        )
                      },
                    },
                    {
                      name: 'ScheduleNewsletterModal',
                      menuItem: {
                        title: t('dashboard.newsletter_editor.save_and_schedule'),
                        icon: <EventIcon className={'fill-text-primary h-[18px] w-[18px]'} />,
                        disabled: !!isRestrictedUser,
                      },
                      type: ButtonWithDropdownTypeEnum.Button,
                      onActionRender: (opened, onClose, key) => {
                        return (
                          <ScheduleNewsletterModal
                            title={t('dashboard.newsletter.schedule_modal_title')}
                            opened={opened}
                            isTagsSelected={isTagsSelected}
                            onClose={onClose}
                            key={key}
                            scheduledAt={newsletterData.scheduledAt}
                            newsletterId={newsletterId}
                            onSave={() => onSave(tempState, { throwError: true })}
                            mutateNewslettersData={mutateNewslettersData}
                          />
                        )
                      },
                    },
                  ]}
                  disabled={isLoading}
                  onClick={() => onSave(tempState)}
                  title={t('global.save_draft')}
                  isFetching={isFetching}
                />
              </>
            )}
            <NeutralButton as={LinkWithoutPrefetch} href={getNewslettersListNextjsUrl()}>
              <CloseIcon className={'h-[20px] w-[20px]'} />
              <span className="hidden lg:flex">{t('global.exit')}</span>
            </NeutralButton>
          </div>
        </div>
        <DashboardMailEditor
          attachmentsTempState={attachmentsTempState}
          contactsSettings={contactsSettings}
          disabled={newsletterData.sent}
          errors={errors}
          isPreFetching={isLoading}
          isSwitchEditorButtonShown
          onChangeContactsSettings={setContactsSettings}
          onChangeEmail={onChangeEmail}
          setAttachmentsTempState={setAttachmentsTempState}
          tempState={tempState}
          withContactsSettings={true}
          mailingId={newsletterData.mailing.id}
        />
      </div>
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.newsletter_editor.confirmation_message_before_exit')}
        hideModal={isDataSaved || newsletterData.sent}
      />
      {UnsavedEmailConfirmationModal}
    </>
  )
}
